/**
 * The Cameras app: what you know, and what you do with it.
 *
 * These were three controls in the action bar, and the bar is for what you are
 * doing right now — stopping, pulling somebody over, driving to a job. None of
 * these is that. Deciding whether to route around cameras, filing what you have
 * seen, and looking at what other people have filed are all things you do
 * sitting still, looking at a screen, which is what the phone is.
 *
 * ## "See all (files your 2)" was two actions wearing one label
 *
 * It filed your sightings *and* turned on the community map, in one press, and
 * said so in a parenthesis. Those are separate: **filing is publishing**. It
 * takes what you drove past and puts it on a map other people read, which is
 * a thing to decide on purpose and not a side effect of wanting to look at
 * something.
 *
 * So they are two rows. One reports, and says how many are new since last time.
 * One draws what everybody has reported, and is a toggle like any other view.
 */

export interface CamerasHandlers {
  toggleAvoid: () => void;
  file: () => void;
  toggleCommunity: () => void;
}

export interface CamerasState {
  found: number;
  avoiding: boolean;
  /** How many of yours have never been filed. */
  unfiled: number;
  showingCommunity: boolean;
  /** How many markers the community map holds, once it has been fetched. */
  reported: number;
}

export class CamerasApp {
  private readonly body: HTMLElement;
  private readonly handlers: CamerasHandlers;
  private last: CamerasState | null = null;

  constructor(handlers: CamerasHandlers) {
    this.handlers = handlers;
    this.body = must('cameras-body');

    this.body.addEventListener('click', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('[data-does]');
      const does = row?.dataset.does;
      if (does === 'avoid') this.handlers.toggleAvoid();
      else if (does === 'file') this.handlers.file();
      else if (does === 'community') this.handlers.toggleCommunity();
    });
  }

  show(state: CamerasState): void {
    this.last = state;
    this.body.replaceChildren();

    const count = document.createElement('div');
    count.className = 'cameras-count';
    count.textContent =
      state.found === 0
        ? 'You have not spotted any cameras yet.'
        : `${state.found} camera${state.found === 1 ? '' : 's'} spotted.`;
    this.body.append(count);

    if (state.found === 0) {
      const note = document.createElement('div');
      note.className = 'garage-note';
      note.textContent = 'Drive past one slowly to find it.';
      this.body.append(note);
      return;
    }

    this.body.append(
      this.row(
        'avoid',
        'Route around them',
        state.avoiding
          ? 'On. Journeys take longer, and only the ones you have found are avoided.'
          : 'Off. The shortest way, past whatever is on it.',
        state.avoiding,
      ),
      // Filing is publishing, and it gets its own row and its own sentence.
      this.row(
        'file',
        'File what you have seen',
        state.unfiled > 0
          ? `${state.unfiled} of yours have never been filed. Filing puts them on the map ` +
              'everybody reads.'
          : 'Everything you have found is already filed.',
        false,
        state.unfiled === 0,
      ),
      this.row(
        'community',
        'Show what others filed',
        state.showingCommunity
          ? state.reported > 0
            ? `${state.reported} reported. Only the ones you have not found yourself are drawn.`
            : 'Nobody has filed anything yet.'
          : 'Cameras other people say are out there. Weaker evidence than your own.',
        state.showingCommunity,
      ),
    );
  }

  /** Repaint from the last state, for when something else changed underneath. */
  refresh(): void {
    if (this.last) this.show(this.last);
  }

  private row(
    does: string,
    title: string,
    detail: string,
    on: boolean,
    disabled = false,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = disabled ? 'setting-row off' : 'setting-row';
    row.dataset.does = does;

    const text = document.createElement('div');
    text.className = 'setting-text';
    const heading = document.createElement('div');
    heading.className = 'setting-title';
    heading.textContent = title;
    const said = document.createElement('div');
    said.className = 'setting-detail';
    said.textContent = detail;
    text.append(heading, said);
    row.append(text);

    // Filing is an act rather than a state, so it gets no switch. A switch
    // implies it can be turned back off, and nothing unpublishes.
    if (does !== 'file') {
      const knob = document.createElement('span');
      knob.className = on ? 'setting-switch on' : 'setting-switch';
      knob.setAttribute('role', 'switch');
      knob.setAttribute('aria-checked', String(on));
      row.append(knob);
    }
    return row;
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
